import React, { useEffect, useRef, useState } from "react";
import { FaPaperPlane, FaTimes } from "react-icons/fa";
import "../style/ChatBotBubble.css";

const CONTACT_SECTION_LINK = "/contact#contatti";
const CONTACT_EMAIL = "info@kyrosgames.com";
const CONTACT_EMAIL_LINK = `mailto:${CONTACT_EMAIL}`;

const initialMessages = [
  {
    id: "greeting",
    from: "bot",
    text: "Ciao! Sono Kyros Bot. Posso orientarti su giochi Kyros, launcher, account, acquisti o community. Non inserire password o dati di pagamento in chat."
  }
];

const quickReplies = [
  "Giochi Kyros",
  "Problemi launcher",
  "Account e libreria",
  "Acquisti e rimborsi"
];

const normalizeInput = (value = "") => {
  const base = String(value).toLowerCase();
  if (typeof base.normalize === "function") {
    return base.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  }
  return base;
};

const includesAny = (text, terms) => terms.some(term => text.includes(term));

const buildResponse = input => {
  const normalized = normalizeInput(input);
  const contactMessage = (
    <>
      Non ho la risposta pronta: visita la pagina Contatti <a href={CONTACT_SECTION_LINK}>qui</a> oppure scrivici a{" "}
      <a href={CONTACT_EMAIL_LINK}>{CONTACT_EMAIL}</a>.
    </>
  );

  if (
    includesAny(normalized, [
      "chi siete",
      "chi e kyros",
      "chi e kyros games",
      "cosa e kyros"
    ])
  ) {
    return "Siamo Kyros Games: produciamo videogame per Windows e li distribuiamo tramite Store diretto, account Kyros e launcher ufficiale.";
  }

  if (
    includesAny(normalized, ["prodotti", "giochi", "titoli", "portfolio"]) &&
    includesAny(normalized, ["avete", "offrite", "svilupp", "fate", "lavorato", "kyros"])
  ) {
    return "I giochi Kyros sono Zombie Crushers, WarFront e Ghost Hunter. Zombie Crushers è gratis, WarFront passa dallo Store e Ghost Hunter è ancora in sviluppo.";
  }

  if (includesAny(normalized, ["launcher", "catalogo", "store", "hub", "piattaforma"])) {
    return "Store, account e launcher servono a una cosa semplice: aggiungi o compri un gioco Kyros, lo ritrovi in libreria e lo installi su Windows.";
  }

  if (includesAny(normalized, ["account", "libreria", "licenza", "login", "accesso"])) {
    return "Per account, libreria e licenze usa la pagina Account. Se non ritrovi un gioco, scrivi al supporto usando la stessa email dell'account Kyros.";
  }

  if (includesAny(normalized, ["acquisti", "acquisto", "rimborso", "rimborsi", "pagamento"])) {
    return "Per acquisti e rimborsi controlla prima la pagina Termini e rimborso. Se serve assistenza, indica gioco, account usato e cosa non torna.";
  }

  if (
    normalized.includes("zombie") &&
    includesAny(normalized, ["scarica", "scarico", "scaricare", "download", "installa"])
  ) {
    return "Puoi scaricare Zombie Crushers da https://kyrosgames.com/games/zombie-crushers oppure dal link diretto https://kyros-launcher-enterprise-929633566631.s3.eu-south-1.amazonaws.com/installer/KyrosSetup.exe.";
  }

  if (
    normalized.includes("zombie") &&
    includesAny(normalized, [
      "come si gioca",
      "come giocare",
      "come funziona",
      "gameplay",
      "guida"
    ])
  ) {
    return "Zombie Crushers è un survival arcade per launcher Kyros: run brevi, sopravvivenza e classifica pubblica.";
  }

  if (includesAny(normalized, ["warfront", "fps", "premium"])) {
    return "WarFront è il titolo premium nello Store Kyros. Prima dell'acquisto trovi scheda, requisiti, licenza account e supporto.";
  }

  if (includesAny(normalized, ["ghost", "ghost hunter", "playtest", "test"])) {
    return "Ghost Hunter è un progetto Kyros in sviluppo. Puoi seguirne lo stato e i prossimi test dalla pagina dedicata e dai canali community.";
  }

  if (includesAny(normalized, ["servizi", "service", "cosa fate", "supporto", "sviluppo"])) {
    return "Kyros non pubblica un catalogo di terzi: qui segui i videogame autoprodotti Kyros, con Store, launcher e supporto collegati.";
  }

  if (includesAny(normalized, ["proporre", "proposta", "pitch", "studio", "build"])) {
    return "Al momento Kyros è concentrata sui propri giochi autoprodotti. Per richieste stampa, video o supporto puoi usare la pagina Contatti.";
  }

  if (includesAny(normalized, ["contatt", "parlare", "chiamare", "scrivervi", "creator", "stampa"])) {
    return (
      <>
        Puoi contattarci a <a href={CONTACT_EMAIL_LINK}>{CONTACT_EMAIL}</a> o compilare il form nella pagina Contatti <a href={CONTACT_SECTION_LINK}>qui</a>.
      </>
    );
  }

  return contactMessage;
};

export default function ChatBotBubble({ open, onClose }) {
  const [messages, setMessages] = useState(() => [...initialMessages]);
  const [inputValue, setInputValue] = useState("");
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const timeoutRef = useRef(null);
  const openRef = useRef(open);

  useEffect(() => {
    openRef.current = open;

    if (!open) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setMessages(() => [...initialMessages]);
      setInputValue("");
      return;
    }

    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
      inputRef.current?.focus();
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  const handleSubmit = event => {
    event.preventDefault();
    const text = inputValue.trim();
    if (!text) return;

    const createdAt = Date.now().toString();
    setMessages(prev => [...prev, { id: `${createdAt}-user`, from: "user", text }]);
    setInputValue("");

    timeoutRef.current = setTimeout(() => {
      if (!openRef.current) {
        timeoutRef.current = null;
        return;
      }
      setMessages(prev => [
        ...prev,
        { id: `${Date.now()}-bot`, from: "bot", text: buildResponse(text) }
      ]);
      timeoutRef.current = null;
    }, 450);
  };

  const handleQuickReply = reply => {
    setInputValue(reply);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  if (!open) return null;

  return (
    <div className="chatbot-bubble" role="dialog" aria-live="polite">
      <div className="chatbot-bubble__header">
        <span>Kyros Bot</span>
        <button type="button" onClick={onClose} aria-label="Chiudi chat">
          <FaTimes />
        </button>
      </div>

      <div className="chatbot-bubble__messages" ref={scrollRef}>
        {messages.map(message => (
          <div
            key={message.id}
            className={`chatbot-bubble__message chatbot-bubble__message--${message.from}`}
          >
            <p>{message.text}</p>
          </div>
        ))}
      </div>

      <div className="chatbot-bubble__quick-replies" aria-label="Risposte rapide">
        {quickReplies.map(reply => (
          <button key={reply} type="button" onClick={() => handleQuickReply(reply)}>
            {reply}
          </button>
        ))}
      </div>

      <form className="chatbot-bubble__form" onSubmit={handleSubmit}>
        <input
          ref={inputRef}
          value={inputValue}
          onChange={event => setInputValue(event.target.value)}
          placeholder="Scrivi un messaggio..."
          aria-label="Scrivi un messaggio"
        />
        <button type="submit" aria-label="Invia messaggio">
          <FaPaperPlane />
        </button>
      </form>
    </div>
  );
}
